/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */

import {
  type HeidiSavedAuth,
  type MedicalDirectorExternal,
  type MedicalDirectorPractitionerDetails,
  type MedicalDirectorSidebarDemographics,
  type MedicalDirectorSidebarEnvoyCallbacks,
  type MedicalDirectorSidebarEvent,
  type MedicalDirectorWindow,
} from '@/integrations/medical-director/types';
import { logger } from '@/utils/logger/logger';

export class MedicalDirectorSidebarPublisher {
  subscribers: any[];

  constructor() {
    this.subscribers = [];
  }

  public deliver(data: MedicalDirectorSidebarEvent) {
    for (let i = 0; i < this.subscribers.length; i++) {
      this.subscribers[i](data);
    }
    return this;
  }
}

class MedicalDirectorSidebarEsp {
  onMDEvent: MedicalDirectorSidebarPublisher;

  constructor() {
    this.onMDEvent = new MedicalDirectorSidebarPublisher();
  }

  public processEspEvent(data: MedicalDirectorSidebarEvent) {
    this.onMDEvent.deliver(data);
  }
}

class MedicalDirectorSidebarEvents {
  public subscribe(
    pub: MedicalDirectorSidebarPublisher,
    callback: (data: MedicalDirectorSidebarEvent) => void
  ) {
    pub.subscribers.push(callback);
    return this;
  }

  public unsubscribe(
    pub: MedicalDirectorSidebarPublisher,
    callback: (data: MedicalDirectorSidebarEvent) => void
  ) {
    pub.subscribers = pub.subscribers.filter((cb) => cb !== callback);
    return this;
  }
}

class MedicalDirectorSidebarEnvoy {
  callbacks: MedicalDirectorSidebarEnvoyCallbacks;

  constructor(callbacks: MedicalDirectorSidebarEnvoyCallbacks) {
    this.callbacks = callbacks;
  }

  public processDemographicsResponse(data: MedicalDirectorSidebarDemographics) {
    // logger.debug('[MD]', 'Demographics response', data);

    if (this.callbacks.processDemographicsResponse) {
      this.callbacks.processDemographicsResponse(data);
    }
  }

  public processPractitionerResponse(data: MedicalDirectorPractitionerDetails) {
    // logger.debug('[MD]', 'Practitioner response', data);

    if (this.callbacks.processPractitionerResponse) {
      this.callbacks.processPractitionerResponse(data);
    }
  }
}

export class MedicalDirectorSidebar {
  External: MedicalDirectorExternal;

  Publisher: MedicalDirectorSidebarPublisher;
  Events: MedicalDirectorSidebarEvents;
  Esp: MedicalDirectorSidebarEsp;
  Envoy: MedicalDirectorSidebarEnvoy;

  constructor(callbacks: MedicalDirectorSidebarEnvoyCallbacks) {
    const mdWindow: MedicalDirectorWindow = window as unknown as MedicalDirectorWindow;
    mdWindow.Sidebar = this;

    // logger.debug(JSON.stringify(mdWindow.external));
    // logger.debug(mdWindow.chrome.webview.hostObjects.sync.external);

    this.External = mdWindow.chrome.webview.hostObjects.sync.external;
    this.Publisher = new MedicalDirectorSidebarPublisher();
    this.Events = new MedicalDirectorSidebarEvents();
    this.Esp = new MedicalDirectorSidebarEsp();
    this.Envoy = new MedicalDirectorSidebarEnvoy(callbacks);
  }

  send(type: string, data: any) {
    // logger.debug('[MD]', 'Sending', type, data);
    this.External.EnvoySend(type, JSON.stringify(data), false);
  }

  addProgressNote(
    patientId: number,
    body: string,
    header: string = 'Heidi',
    lineNumber: number = 1,
    section: string = 'Heidi'
  ) {
    logger.debug('[MD]', 'Adding progress note');
    this.External.AddProgressNote(patientId, header, body, lineNumber, section);
  }

  static isMedicalDirector() {
    const mdWindow: MedicalDirectorWindow = window as unknown as MedicalDirectorWindow;

    try {
      if (
        typeof mdWindow.chrome.webview.hostObjects.sync.external.EnvoySend !== 'undefined'
      ) {
        mdWindow.external = mdWindow.chrome.webview.hostObjects.sync.external;
        return true;
      }
    } catch {
      // ignore
    }

    return false;
  }

  readPropertyBagValue(id: PropertyBagValue) {
    return this.External.readPropertyBagValue(id);
  }

  layoutWidget() {
    this.External.layoutWidget();
  }

  saveSetting<T>(key: string, value: T) {
    this.External.SaveSetting('heidi:' + key, JSON.stringify(value));
  }

  getSettings<T>(key: string) {
    const settings = this.External.GetSettings('heidi:' + key);
    if (settings) {
      return JSON.parse(settings) as T;
    }
    return undefined;
  }

  openPatient(newPatientId: string) {
    this.External.OpenPatient(newPatientId);
  }
}

export enum PropertyBagValue {
  CURRENT_USER_ID = 'userid',
  CURRENT_PATIENT_ID = 'hcn.md3.openpatient.id',
}

// MD-only: persists auth state in the MD Sidebar framework's user settings
// store, which survives WebView2 data clearing (the reason Kinde/Auth0 cookies
// don't survive when MD Clinical closes the widget). Outside MD these helpers
// are safe no-ops.
const MD_AUTH_SETTING_KEY = 'auth';

export function getPersistedMdAuthToken(): HeidiSavedAuth | null {
  if (typeof window === 'undefined') return null;
  if (!MedicalDirectorSidebar.isMedicalDirector()) return null;

  try {
    const mdWindow: MedicalDirectorWindow = window as unknown as MedicalDirectorWindow;
    const raw = mdWindow.external.GetSettings('heidi:' + MD_AUTH_SETTING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HeidiSavedAuth;
    if (!parsed?.token) return null;
    return parsed;
  } catch (error) {
    logger.error('[MD]', 'Failed to read persisted auth token', error);
    return null;
  }
}

export function setPersistedMdAuthToken(value: HeidiSavedAuth): void {
  if (typeof window === 'undefined') return;
  if (!MedicalDirectorSidebar.isMedicalDirector()) return;

  try {
    const mdWindow: MedicalDirectorWindow = window as unknown as MedicalDirectorWindow;
    mdWindow.external.SaveSetting('heidi:' + MD_AUTH_SETTING_KEY, JSON.stringify(value));
  } catch (error) {
    logger.error('[MD]', 'Failed to write persisted auth token', error);
  }
}

export function clearPersistedMdAuthToken(): void {
  setPersistedMdAuthToken({ token: null, expiresAt: null, authProvider: null });
}
